import { getAdditionalEnrollmentStatuses } from 'utils/enrollmentStatuses';
import React from 'react';
import {
  Box,
  Flex,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { BiImport } from 'react-icons/bi';
import { HiUsers } from 'react-icons/hi';
import { IoMdCloudUpload } from 'react-icons/io';

import xhr from 'xhr';
import BasicModal from 'components/modal/Modal';
import RecipientsModal from './RecipientsModal';
import useAuth from 'hooks/useAuth';

const PROGRAM_FIELDS = ['grade', 'technical_career', 'course'];
const TABS = ['filter', 'file', 'prospects'];

const emptyFilters = { program_type: '', program: '', group: '', status: '', semester: '', period: '' };

const relationId = (value) => (value && typeof value === 'object' ? value.id : value) || '';

const PERIOD_OPTIONS = ['P1', 'P2', 'P3', 'P4'];

/** Filtros del modal a partir de la campaña guardada. */
const filtersFromCampaign = (campaign) => {
  const programType = campaign.program_type;
  if (!programType || !programType.id) return emptyFilters;

  return {
    program_type: programType.id,
    program: relationId(campaign[programType.type]),
    group: campaign.group === 'all' ? '' : relationId(campaign.group),
    status: campaign.student_status || '',
    semester: relationId(campaign.semester),
    period: campaign.period || '',
  };
};

/** Payload de /email-campaigns/filter/students (usa ids). */
const buildFilterPayload = (filters, programType) => ({
  program_type: programType.id,
  program: filters.program ? Number(filters.program) : null,
  group: filters.group ? Number(filters.group) : null,
  student_status: filters.status || null,
  semester: filters.semester ? Number(filters.semester) : null,
  period: programType.type === 'technical_career' ? filters.period || null : null,
});

const Receivers = (props) => {
  const { activeTab, setActiveTab, setCampaign, campaign } = props;
  const { user } = useAuth();
  const [programTypes, setProgramTypes] = React.useState([]);
  const [programs, setPrograms] = React.useState([]);
  const [groups, setGroups] = React.useState([]);
  const [semesters, setSemesters] = React.useState([]);
  const [filters, setFilters] = React.useState(emptyFilters);
  const [previewCount, setPreviewCount] = React.useState(null);
  const [isCounting, setIsCounting] = React.useState(false);

  const [allProspects, setAllProspects] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [isFetching, setIsFetching] = React.useState(true);
  const [isImporting, setIsImporting] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [recipientsPayload, setRecipientsPayload] = React.useState(null);

  let buttonRef = React.useRef();

  const selectedProgramType = programTypes.find((el) => el.id === Number(filters.program_type));
  const isTechnicalCareer = selectedProgramType?.type === 'technical_career';

  React.useEffect(() => {
    const getStudents = async () => {
      try {
        setIsFetching(true);

        const { campaign, setCampaign } = props;

        if (campaign && campaign.id) {
          let payload = {};

          if (campaign.file) {
            payload = { file: campaign.file };
          }

          if (campaign.program_type && campaign.program_type.id) {
            payload = buildFilterPayload(filtersFromCampaign(campaign), campaign.program_type);
          }

          if (!Object.keys(payload).length) return;

          const response = await xhr.post('/email-campaigns/filter/students', {
            data: payload,
          });

          setCampaign({ ...campaign, students: response.data.data });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    };

    getStudents();
  }, [props.campaign.id]);

  React.useEffect(() => {
    const getProgramTypes = async () => {
      const programTypesResponse = await xhr('/program-types');
      setProgramTypes(programTypesResponse.data.data);
    };

    getProgramTypes();
  }, []);

  // Los semestres no dependen del programa: se cargan siempre (igual que en Filtro Estudiantes).
  React.useEffect(() => {
    const getSemesters = async () => {
      try {
        let url = '/semesters?sort[0]=createdAt:desc&pagination[limit]=-1';
        if (user?.school) {
          url += `&filters[school][id][$eq]=${user.school.id}`;
        } else {
          url += '&filters[$or][0][school][id][$null]=true&filters[$or][1][school][id][$eq]=0';
        }
        const response = await xhr(url);
        setSemesters(response.data.data);
      } catch (error) {
        console.error(error);
        setSemesters([]);
      }
    };

    getSemesters();
  }, [user?.school]);

  React.useEffect(() => {
    setPrograms([]);
    if (!selectedProgramType) return;

    const getPrograms = async () => {
      try {
        const response = await xhr(
          `/${selectedProgramType.type.replace('_', '-')}s?filters[program_type][id][$eq]=${selectedProgramType.id}&filters[active][$eq]=true&pagination[limit]=-1`
        );
        setPrograms(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    getPrograms();
  }, [selectedProgramType]);

  React.useEffect(() => {
    setGroups([]);
    if (!selectedProgramType || !filters.program) return;

    const getGroups = async () => {
      try {
        const response = await xhr(
          `/class-groups?filters[${selectedProgramType.type}][id][$eq]=${filters.program}&filters[active][$eq]=true&pagination[limit]=-1`
        );
        setGroups(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };

    getGroups();
  }, [selectedProgramType, filters.program]);

  // Conteo en vivo de destinatarios mientras se ajustan los filtros.
  React.useEffect(() => {
    if (!isOpen || activeTab !== 'filter' || !selectedProgramType) {
      setPreviewCount(null);
      return undefined;
    }

    let cancelled = false;
    setIsCounting(true);

    const timeout = setTimeout(async () => {
      try {
        const response = await xhr.post('/email-campaigns/filter/students', {
          data: buildFilterPayload(filters, selectedProgramType),
        });
        if (!cancelled) setPreviewCount(response.data.data);
      } catch (error) {
        console.error(error);
        if (!cancelled) setPreviewCount(null);
      } finally {
        if (!cancelled) setIsCounting(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [isOpen, activeTab, filters, selectedProgramType]);

  const updateFilter = (field, value) => {
    const next = { ...filters, [field]: value };
    if (field === 'program_type') {
      next.program = '';
      next.group = '';
      const programType = programTypes.find((el) => el.id === Number(value));
      if (programType?.type !== 'technical_career') next.period = '';
        if (['desercion', 'aplazado', 'no_aprobado', 'promocion_pendiente'].includes(next.status) &&
            !getAdditionalEnrollmentStatuses(programType?.type).some((o) => o.value === next.status)) next.status = '';
    }
    if (field === 'program') next.group = '';
    setFilters(next);
  };

  const onDrop = React.useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      if (!file.name.includes('.csv')) return window.alert('Formato de archivo no soportado. Debs adjuntar un csv');

      setFile(file);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  // El modal abre siempre limpio: precargar lo guardado dejaba filtros ocultos
  // (p. ej. un programa inactivo que no aparece en el select pero seguía filtrando).
  const initReceivers = () => {
    setFilters(emptyFilters);
    setFile(null);
    setAllProspects(false);
    setActiveTab('filter');
    setIsOpen(true);
  };

  const getEmailStudents = async () => {
    try {
      const { campaign } = props;

      if (activeTab === 'filter' && !selectedProgramType) return window.alert('Debes elegir tipo de programa');
      if (activeTab === 'file' && !campaign.file && !file) return window.alert('Debes adjuntar un archivo csv');

      setIsImporting(true);

      let payload;

      if (activeTab === 'filter') {
        payload = buildFilterPayload(filters, selectedProgramType);
      }

      if (activeTab === 'prospects') {
        payload = {
          category: props.campaign || '',
          program_type: campaign.programType,
          program: campaign.program,
          prospect_status: campaign.prospect_status,
          global_prospect: allProspects,
          prospect_program: campaign.prospect_program,
          prospect: true,
        };
      }

      if (activeTab === 'file') {
        if (!file && campaign.file) return buttonRef.current.click();

        const formData = new FormData();

        formData.append('files', file);
        formData.append('ref', 'api::email-campaign.email-campaign');
        formData.append('refId', campaign.id);
        formData.append('field', 'file');

        const uploadResponse = await xhr('/upload', {
          method: 'post',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (uploadResponse.data.length) {
          payload = { file: uploadResponse.data[0] };
        }

        if (campaign.file && campaign.file.id) {
          await xhr.delete(`/upload/files/${campaign.file.id}`);
        }
      }

      const responseGlobal =
        payload && (await xhr.post('/email-campaigns/filter/students', { data: payload }));

      let data = {};

      if (activeTab === 'filter') {
        const programFields = Object.fromEntries(
          PROGRAM_FIELDS.map((field) => [field, field === selectedProgramType.type ? payload.program : null])
        );

        const response = await xhr.put(`/email-campaigns/${campaign.id}`, {
          data: {
            program_type: payload.program_type,
            ...programFields,
            student_status: payload.student_status,
            semester: payload.semester,
            period: payload.period,
            group: payload.group,
            receivers: responseGlobal.data.data,
            file: null,
            global: false,
          },
        });

        // La respuesta del PUT no trae relaciones: se reflejan en el estado para reabrir el modal con lo guardado.
        data = {
          ...response.data.data,
          ...Object.fromEntries(
            PROGRAM_FIELDS.map((field) => [field, programFields[field] ? { id: programFields[field] } : null])
          ),
          program_type: selectedProgramType,
          programType: selectedProgramType.id,
          group: payload.group ? { id: payload.group } : null,
          semester: payload.semester ? { id: payload.semester } : null,
          student_status: payload.student_status,
          studentStatus: payload.student_status || '',
          period: payload.period,
          file: null,
        };

        if (campaign.file) await xhr.delete(`/upload/files/${campaign.file.id}`);
      }

      if (activeTab === 'file') {
        const response = await xhr.put(`/email-campaigns/${campaign.id}`, {
          data: {
            program_type: null,
            ...Object.fromEntries(PROGRAM_FIELDS.map((field) => [field, null])),
            student_status: null,
            semester: null,
            period: null,
            group: null,
          },
        });

        data = {
          ...response.data.data,
          program_type: null,
          programType: '',
          file: payload?.file || null,
        };
      }

      props.setCampaign({
        ...campaign,
        students: responseGlobal?.data?.data,
        global_prospect: allProspects,
        ...data,
      });

      setIsOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <Box marginTop="5px">
        <Flex alignItems="center">
          {isFetching ? (
            <Spinner />
          ) : (
            <>
              <Box color="text100">
                <HiUsers />
              </Box>
              <Box marginLeft="10px" color="text100" display="flex">
                <Box use="span" fontWeight="500" mr="5px">
                  {props.campaign.students}
                </Box>{' '}
                estudiantes
              </Box>

              <Button marginLeft="10px" onClick={initReceivers}>
                <BiImport />
                <Box marginLeft="5px" fontSize="14px">
                  Añadir
                </Box>
              </Button>
            </>
          )}
        </Flex>
      </Box>

      <BasicModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        // Con el modal de destinatarios encima, este no debe bloquear su scroll (Chakra solo deja scroll al primero).
        blockScrollOnMount={!recipientsPayload}
      >
        <Box>
          <Box fontWeight="bold" fontSize="28px">
            Añadir estudiantes o prospectos
          </Box>

          <Tabs index={Math.max(TABS.indexOf(activeTab), 0)} onChange={(index) => setActiveTab(TABS[index])}>
            <TabList>
              <Tab>Filtrar</Tab>
              <Tab>Archivo CSV</Tab>
              <Tab>Prospectos</Tab>
            </TabList>

            <TabPanels marginTop="10px">
              <TabPanel>
                <Select
                  value={filters.program_type}
                  placeholder="Tipo de programa"
                  onChange={(event) => updateFilter('program_type', event.target.value)}
                >
                  {programTypes.map((el) => (
                    <option value={el.id} key={el.id}>
                      {el.name}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filters.program}
                  marginTop="10px"
                  placeholder="Programa"
                  onChange={(event) => updateFilter('program', event.target.value)}
                >
                  {programs.map((el) => (
                    <option value={el.id} key={el.id}>
                      {el.name}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filters.group}
                  marginTop="10px"
                  placeholder="Grupo"
                  onChange={(event) => updateFilter('group', event.target.value)}
                >
                  {groups.map((el) => (
                    <option value={el.id} key={el.id}>
                      {el.name}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filters.semester}
                  marginTop="10px"
                  placeholder="Semestre"
                  onChange={(event) => updateFilter('semester', event.target.value)}
                >
                  {semesters.map((el) => (
                    <option value={el.id} key={el.id}>
                      {el.name}
                    </option>
                  ))}
                </Select>

                {isTechnicalCareer && (
                  <Select
                    value={filters.period}
                    marginTop="10px"
                    placeholder="Semestre cursando"
                    onChange={(event) => updateFilter('period', event.target.value)}
                  >
                    {PERIOD_OPTIONS.map((period) => (
                      <option value={period} key={period}>
                        {period}
                      </option>
                    ))}
                  </Select>
                )}

                <Select
                  value={filters.status}
                  marginTop="10px"
                  placeholder="Estado"
                  onChange={(event) => updateFilter('status', event.target.value)}
                >
                  <option value="registrado">Registrado</option>
                  <option value="estudiando">Estudiando</option>
                  <option value="promovido">Promovido</option>
                  <option value="graduado">Graduado</option>
                  <option value="retirado">Retirado</option>
                  {getAdditionalEnrollmentStatuses(selectedProgramType?.type).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Select>

                <Flex alignItems="center" marginTop="16px" color="gray.700" gap="8px">
                  <HiUsers />
                  {!selectedProgramType ? (
                    <Text fontSize="sm" color="gray.500">
                      Selecciona un tipo de programa para ver los destinatarios.
                    </Text>
                  ) : isCounting ? (
                    <Spinner size="sm" />
                  ) : (
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="bold">
                        {previewCount ?? '—'}
                      </Text>{' '}
                      estudiantes recibirán esta campaña
                    </Text>
                  )}
                  {selectedProgramType && !isCounting && previewCount !== null && (
                    <Button
                      size="xs"
                      variant="link"
                      colorScheme="purple"
                      marginLeft="6px"
                      onClick={() => setRecipientsPayload(buildFilterPayload(filters, selectedProgramType))}
                    >
                      Ver destinatarios
                    </Button>
                  )}
                </Flex>
              </TabPanel>

              <TabPanel>
                <Box
                  {...getRootProps({ className: 'dropzone' })}
                  backgroundColor="white700"
                  borderRadius="8px"
                  padding="major-3"
                  cursor="pointer"
                >
                  <Input {...getInputProps()} />

                  <Box
                    color="text100"
                    border="1px solid"
                    borderColor="gray200"
                    borderRadius="8px"
                    borderStyle="dashed"
                    padding="10px"
                    textAlign="center"
                  >
                    <Box>
                      <Box fontSize="28px" color="gray.500" display="flex" justifyContent="center">
                        <IoMdCloudUpload size={40} />
                      </Box>
                      <Box>Arrastra y suelta el archivo</Box>
                    </Box>

                    <Box marginY="10px">ó</Box>

                    <Box color="primary">Selecciona el archivo</Box>
                  </Box>
                </Box>

                {(props.campaign.file || file) && (
                  <Box fontWeight="500" marginTop="10px">
                    {(props.campaign.file && props.campaign.file.name) || file.name}
                  </Box>
                )}
              </TabPanel>

              <TabPanel>
                <Select
                  value={allProspects}
                  onChange={(e) => setAllProspects(e.target.value === 'true' ? true : false)}
                  mb="10px"
                >
                  <option value={false}>Opcional</option>
                  <option value={true}>Todos los prospectos</option>
                </Select>
                {!allProspects && (
                  <>
                    <Select
                      value={props.campaign.prospect_status || ''}
                      marginTop="10px"
                      placeholder="Estado"
                      onChange={(event) => {
                        props.setCampaign({
                          ...props.campaign,
                          prospect_status: event.target.value,
                        });
                      }}
                    >
                      <option value={'fallido'}>Fallido</option>
                      <option value={'basura'}>Basura</option>
                      <option value={'registrado'}>Registrado</option>
                      <option value={'estudiando'}>Estudiando</option>
                      <option value={'nuevo'}>Nuevo</option>
                      <option value={'intentando_contactar'}>Intentando Contactar</option>
                      <option value={'en_curso_tibio'}>En curso tibio</option>
                      <option value={'en_curso_caliente'}>En curso caliente</option>
                      <option value={'no_interesado'}>No interesado</option>
                    </Select>

                    <Input
                      mt="15px"
                      placeholder="Programa (slug) ej: bachillerato-para-adultos"
                      onChange={(e) =>
                        setCampaign({
                          ...campaign,
                          prospect_program: e.target.value,
                        })
                      }
                      value={campaign.prospect_program}
                    />
                  </>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Button
            marginTop="10px"
            palette="primary"
            onClick={getEmailStudents}
            isLoading={isImporting}
            disabled={isImporting}
          >
            Guardar
          </Button>

          <Button
            ref={buttonRef}
            marginLeft="10px"
            onClick={() => {
              setActiveTab('filter');
              setFile(null);
              setIsOpen(false);
            }}
          >
            Cancelar
          </Button>
        </Box>
      </BasicModal>

      <RecipientsModal
        isOpen={Boolean(recipientsPayload)}
        payload={recipientsPayload}
        onClose={() => setRecipientsPayload(null)}
      />
    </>
  );
};

export default Receivers;
